/**
 * Modelo de datos del arbol genealogico.
 *
 * Principio de diseno: este modelo es el *dueno* de la informacion que la
 * aplicacion entiende y manipula (personas, familias, eventos, lugares,
 * nombres con apellidos dobles). Todo lo demas que traiga un fichero Gramps
 * XML -- citas, fuentes, notas, objetos, etiquetas, direcciones... -- se
 * guarda en bruto y se vuelve a escribir tal cual.
 *
 * Eso es lo que hace posible la ida y vuelta sin perdida: no hace falta
 * modelar el formato entero, basta con no tirar nada.
 */

/** Sexo en la notacion de Gramps. OJO: M = hombre, F = mujer. */
export type Sexo = "M" | "F" | "U" | "X";

/**
 * Un elemento XML que no modelamos, conservado literalmente.
 * Es el XML serializado completo, con sus atributos y descendencia.
 */
export type Crudo = string;

/** Hijos en bruto agrupados por nombre de etiqueta, en orden de aparicion. */
export type Otros = Record<string, Crudo[]>;

/**
 * Una fecha de Gramps. El formato admite cuatro formas alternativas
 * (dateval, daterange, datespan, datestr) y cada una tiene sus atributos.
 * Se guardan en bruto para no perder nada, y se interpretan al mostrar.
 */
export interface Fecha {
  /** "dateval" | "daterange" | "datespan" | "datestr" */
  etiqueta: string;
  /** Todos los atributos, literales. */
  atribs: Record<string, string>;
}

/**
 * Un apellido. En la convencion espanola/colombiana una persona tiene dos:
 * el primero del padre (derivation="Patrilineal") y el segundo de la madre
 * (derivation="Matrilineal"). Son DOS elementos <surname> distintos, no un
 * campo de texto unico: de ahi se saca de que rama viene cada apellido.
 */
export interface Apellido {
  texto: string;
  /** prim="1": es el apellido principal, el que se usa para ordenar. */
  prim: boolean;
  /** "Patrilineal" | "Matrilineal" | "Inherited" | ... */
  derivation?: string;
  prefix?: string;
  connector?: string;
  /** Atributos de <surname> que no modelamos. */
  otrosAtribs: Record<string, string>;
}

/** Un nombre completo. Una persona puede tener varios (de nacimiento, de casada...). */
export interface Nombre {
  /** type, alt, priv, sort, display y cualquier otro, literales. */
  atribs: Record<string, string>;
  /** Nombres de pila. Puede faltar: hay personas conocidas solo por el apellido. */
  first?: string;
  call?: string;
  /** Cero, uno o varios apellidos. Cero tambien es valido. */
  apellidos: Apellido[];
  suffix?: string;
  title?: string;
  nick?: string;
  familynick?: string;
  group?: string;
  fecha?: Fecha;
  otros: Otros;
}

/** Referencia a un evento desde una persona o familia. */
export interface RefEvento {
  hlink: string;
  role?: string;
  otrosAtribs: Record<string, string>;
  otros: Otros;
}

/** Referencia a un hijo desde una familia. mrel/frel marcan adopcion, etc. */
export interface RefHijo {
  hlink: string;
  mrel?: string;
  frel?: string;
  otrosAtribs: Record<string, string>;
  otros: Otros;
}

export interface Persona {
  /** Identificador interno, atributo XML de tipo ID. Las referencias usan esto. */
  handle: string;
  /** Identificador visible: I0001. */
  id?: string;
  change: string;
  priv?: string;
  sexo: Sexo;
  nombres: Nombre[];
  eventos: RefEvento[];
  /** Familias donde esta persona es HIJA. */
  childof: string[];
  /** Familias donde esta persona es PADRE o MADRE. */
  parentin: string[];
  otrosAtribs: Record<string, string>;
  otros: Otros;
}

/** Tipo de union de una familia. */
export type TipoUnion = "Married" | "Unmarried" | "Unknown" | "Civil Union" | string;

export interface Familia {
  handle: string;
  id?: string;
  change: string;
  priv?: string;
  /** Puede faltar. */
  rel?: TipoUnion;
  /** Puede faltar: hay familias con madre pero sin padre registrado. */
  padre?: string;
  /** Puede faltar. Y pueden faltar los dos: familia que solo declara hermandad. */
  madre?: string;
  eventos: RefEvento[];
  hijos: RefHijo[];
  otrosAtribs: Record<string, string>;
  otros: Otros;
}

export interface Evento {
  handle: string;
  id?: string;
  change: string;
  priv?: string;
  /** "Birth" | "Death" | "Marriage" | ... */
  tipo?: string;
  fecha?: Fecha;
  /** hlink al lugar. */
  lugar?: string;
  descripcion?: string;
  otrosAtribs: Record<string, string>;
  otros: Otros;
}

/** Nombre de un lugar. El DTD exige al menos uno por placeobj. */
export interface NombreLugar {
  value: string;
  lang?: string;
  fecha?: Fecha;
  otrosAtribs: Record<string, string>;
}

export interface Lugar {
  handle: string;
  id?: string;
  change: string;
  priv?: string;
  /** Atributo obligatorio en el DTD: "City", "Country"... */
  tipo: string;
  titulo?: string;
  codigo?: string;
  nombres: NombreLugar[];
  otrosAtribs: Record<string, string>;
  otros: Otros;
}

/** Cabecera del fichero. */
export interface Cabecera {
  creadoFecha: string;
  creadoVersion: string;
  /** researcher y mediapath en bruto: no los tocamos. */
  otros: Otros;
}

/**
 * El arbol completo.
 *
 * `seccionesCrudas` guarda las secciones de primer nivel que no modelamos
 * (name-formats, tags, citations, sources, objects, repositories, notes,
 * bookmarks, namemaps) serializadas tal cual, para devolverlas en su sitio.
 */
export interface Arbol {
  /** Version del XML leido, p.ej. "1.7.2". */
  version: string;
  cabecera: Cabecera;
  personas: Map<string, Persona>;
  familias: Map<string, Familia>;
  eventos: Map<string, Evento>;
  lugares: Map<string, Lugar>;
  /** Atributos del elemento people: default, home. */
  atribsPeople: Record<string, string>;
  seccionesCrudas: Record<string, Crudo>;
}

export function arbolVacio(version = "1.7.2"): Arbol {
  return {
    version,
    cabecera: {
      creadoFecha: new Date().toISOString().slice(0, 10),
      creadoVersion: "6.0.8",
      otros: {},
    },
    personas: new Map(),
    familias: new Map(),
    eventos: new Map(),
    lugares: new Map(),
    atribsPeople: {},
    seccionesCrudas: {},
  };
}

// ---------------------------------------------------------------------------
// Utilidades de lectura sobre el modelo
// ---------------------------------------------------------------------------

/** El nombre que se considera principal: el primero que no sea alternativo. */
export function nombrePrincipal(p: Persona): Nombre | undefined {
  return p.nombres.find((n) => n.atribs["alt"] !== "1") ?? p.nombres[0];
}

/** El apellido marcado prim="1", o el primero que haya. */
export function apellidoPrimario(n: Nombre | undefined): Apellido | undefined {
  if (!n) return undefined;
  return n.apellidos.find((a) => a.prim) ?? n.apellidos[0];
}

/**
 * Nombre completo para mostrar: nombres de pila + apellidos en orden.
 * Soporta los casos reales del archivo: sin segundo apellido, y sin nombre
 * de pila (persona conocida solo por el apellido).
 */
export function nombreCompleto(p: Persona): string {
  const n = nombrePrincipal(p);
  if (!n) return p.id ?? p.handle;
  const partes = [n.first?.trim(), ...n.apellidos.map((a) => a.texto.trim())];
  const texto = partes.filter(Boolean).join(" ").trim();
  return texto || (p.id ?? p.handle);
}

/** Solo los apellidos, en orden, para etiquetas cortas. */
export function soloApellidos(p: Persona): string {
  const n = nombrePrincipal(p);
  if (!n) return "";
  return n.apellidos
    .map((a) => a.texto.trim())
    .filter(Boolean)
    .join(" ");
}

/** Apellido por origen, para poder filtrar por rama. */
export function apellidoPor(
  p: Persona,
  derivacion: "Patrilineal" | "Matrilineal",
): string | undefined {
  const n = nombrePrincipal(p);
  const a = n?.apellidos.find((x) => x.derivation === derivacion);
  return a?.texto.trim() || undefined;
}

/** Fecha legible, o undefined si no hay nada que mostrar. */
export function fechaTexto(f: Fecha | undefined): string | undefined {
  if (!f) return undefined;
  const a = f.atribs;
  switch (f.etiqueta) {
    case "dateval": {
      const v = a["val"];
      if (!v) return undefined;
      const prefijo =
        a["type"] === "before"
          ? "antes de "
          : a["type"] === "after"
            ? "despues de "
            : a["type"] === "about"
              ? "hacia "
              : "";
      const sufijo =
        a["quality"] === "estimated"
          ? " (estimada)"
          : a["quality"] === "calculated"
            ? " (calculada)"
            : "";
      return prefijo + v + sufijo;
    }
    case "daterange":
      return "entre " + (a["start"] ?? "?") + " y " + (a["stop"] ?? "?");
    case "datespan":
      return "de " + (a["start"] ?? "?") + " a " + (a["stop"] ?? "?");
    case "datestr":
      return a["val"];
    default:
      return undefined;
  }
}

/** El ano, si se puede deducir. Sirve para ordenar y para avisar de fechas imposibles. */
export function anoDe(f: Fecha | undefined): number | undefined {
  if (!f) return undefined;
  const bruto = f.atribs["val"] ?? f.atribs["start"] ?? f.atribs["stop"];
  if (!bruto) return undefined;
  const m = /(\d{3,4})/.exec(bruto);
  if (!m?.[1]) return undefined;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : undefined;
}

/** El evento de un tipo dado referenciado por una persona (el primero). */
export function eventoDe(
  arbol: Arbol,
  p: Persona,
  tipo: string,
): Evento | undefined {
  for (const ref of p.eventos) {
    const e = arbol.eventos.get(ref.hlink);
    if (e?.tipo === tipo) return e;
  }
  return undefined;
}
